import { createSlice } from "@reduxjs/toolkit";
import {
  EstimatedTargetLap,
  ProcessedPercentLap,
  TvEnergyMessage,
} from "../types/tvEnergy";
import { TvEnergyData } from "../types/tvEnergy";

const initialState = {
  connectionState: {
    streamLink: "",
    isConnected: false,
    timingFeedOffset: 0,
  },
  data: {},
} as TvEnergyMessage;

function convertProcessedPercentByLap(
  processedPercentByLap: ProcessedPercentLap[] | null | undefined,
): Record<number, number> | null {
  if (!processedPercentByLap) {
    return null;
  }
  const record: Record<number, number> = {};
  for (const item of processedPercentByLap) {
    record[item.lapNumber] = item.processedPercent;
  }
  return record;
}

function convertEstimatedTargetByLap(
  estimatedTargetByLap: EstimatedTargetLap[] | null | undefined,
): Record<number, number> | null {
  if (!estimatedTargetByLap) {
    return null;
  }
  const record: Record<number, number> = {};
  for (const item of estimatedTargetByLap) {
    record[item.lapNumber] = item.estimatedTarget;
  }
  return record;
}

export const tvEnergySlice = createSlice({
  name: "tvEnergy",
  initialState,
  reducers: {
    setConnectionState: (state, action) => {
      if (action.payload.streamLink !== null) {
        state.connectionState.streamLink = action.payload.streamLink;
      }
      if (action.payload.isConnected !== null) {
        state.connectionState.isConnected = action.payload.isConnected;
      }
      if (action.payload.timingFeedOffset !== null) {
        state.connectionState.timingFeedOffset =
          action.payload.timingFeedOffset;
      }
    },
    resetTvEnergyData: (state) => {
      state.data = {};
    },

    setTvEnergyData: (state, action) => {
      const tvEnergyData: TvEnergyData = {};
      for (const driverEnergy of action.payload.drivers) {
        tvEnergyData[driverEnergy.driver] = {
          driverName: driverEnergy.driverName,
          timestamp: driverEnergy.timestamp,
          tvPercent: driverEnergy.tvPercent,
          processedPercent: driverEnergy.processedPercent,
          estimatedTarget: driverEnergy.estimatedTarget,
          deltaToRedCar: driverEnergy.deltaToRedCar,
          deltaToBlueCar: driverEnergy.deltaToBlueCar,
          deltaToLeader: driverEnergy.deltaToLeader,
          processedPercentByLap: convertProcessedPercentByLap(
            driverEnergy.processedPercentByLap,
          ),
          estimatedTargetByLap: convertEstimatedTargetByLap(
            driverEnergy.estimatedTargetByLap,
          ),
        };
      }
      state.data = tvEnergyData;
    },

    setDriverEnergy: (state, action) => {
      const driver = action.payload.driver;
      if (!state.data[driver]) {
        state.data[driver] = {
          driverName: action.payload.driverName || "",
          timestamp: action.payload.timestamp || null,
          tvPercent: action.payload.tvPercent || null,
          processedPercent: action.payload.processedPercent || null,
          estimatedTarget: action.payload.estimatedTarget || null,
          deltaToRedCar: action.payload.deltaToRedCar || null,
          deltaToBlueCar: action.payload.deltaToBlueCar || null,
          deltaToLeader: action.payload.deltaToLeader || null,
          processedPercentByLap: {},
          estimatedTargetByLap: {},
        };
      }

      if (action.payload.driverName != null) {
        state.data[driver].driverName = action.payload.driverName;
      }
      if (action.payload.timestamp != null) {
        state.data[driver].timestamp = action.payload.timestamp;
      }
      if (action.payload.tvPercent != null) {
        state.data[driver].tvPercent = action.payload.tvPercent;
      }
      if (action.payload.processedPercent != null) {
        state.data[driver].processedPercent = action.payload.processedPercent;
        if (
          action.payload.lapNumber &&
          state.data[driver].processedPercentByLap
        ) {
          state.data[driver].processedPercentByLap[action.payload.lapNumber] =
            action.payload.processedPercent;
        }
      }
      if (action.payload.estimatedTarget != null) {
        state.data[driver].estimatedTarget = action.payload.estimatedTarget;
        if (
          action.payload.lapNumber &&
          state.data[driver].estimatedTargetByLap
        ) {
          state.data[driver].estimatedTargetByLap[action.payload.lapNumber] =
            action.payload.estimatedTarget;
        }
      }
      if (action.payload.deltaToRedCar != null) {
        state.data[driver].deltaToRedCar = action.payload.deltaToRedCar;
      }
      if (action.payload.deltaToBlueCar != null) {
        state.data[driver].deltaToBlueCar = action.payload.deltaToBlueCar;
      }
      if (action.payload.deltaToLeader != null) {
        state.data[driver].deltaToLeader = action.payload.deltaToLeader;
      }
    },
  },
});

export const {
  setConnectionState,
  setTvEnergyData,
  setDriverEnergy,
  resetTvEnergyData,
} = tvEnergySlice.actions;
export default tvEnergySlice.reducer;
